import React from 'react';
import moment from 'moment';
import { Box, Flex, Text, ScrollArea } from '@mantine/core';
import type { ActiveFilter } from '../../../common/filters/types';
import { capitalizeFirstLetter } from '../../../../utils/capitalizeFirstLetter';
import { formatBigMonetaryValue } from '../../../ideaNiv/Funcs';



function formatDateValue(value: string): string {
  const m = moment(value);
  return m.isValid() ? m.format('MM/DD/YYYY') : value;
}

/**
 * Format dynamic date keys to user-friendly labels
 */
const formatDynamicDateLabel = (key: string): string => {
  const labels: Record<string, string> = {
    thisWeek: 'This Week',
    lastWeek: 'Last Week',
    nextWeek: 'Next Week',
    thisMonth: 'This Month',
    lastMonth: 'Last Month',
    nextMonth: 'Next Month',
    thisQuarter: 'This Quarter',
    lastQuarter: 'Last Quarter',
    nextQuarter: 'Next Quarter',
    thisYear: 'This Year',
    lastYear: 'Last Year',
    nextYear: 'Next Year',
  };
  return labels[key] || key;
};

/**
 * Format special checkbox filter values to user-friendly labels
 */
const formatCheckboxValue = (value: string): string => {
  const specialLabels: Record<string, string> = {
    unassigned: 'Unassigned',
    not_set: 'Not Set',
    set: 'Set',
  };
  return specialLabels[value] || value;
};

/**
 * Check if a filter is a change log filter based on dbFieldPath pattern
 */
const isChangeLogFilter = (filter: any): boolean => {
  return filter?.dbFieldPath?.includes('.itemChangeLog[') && 
         filter?.operator?.match?.(/^\$(gt|gte|lt|lte):.+$/);
};

/**
 * Format change log filter for display
 */
const formatChangeLogFilterLabel = (filter: any): string => {
  if (filter.label) return filter.label;
  
  const dbFieldPath = filter.dbFieldPath || '';
  const match = dbFieldPath.match(/\.itemChangeLog\[([^|]+)\|[^|]+\|([^\]]+)\]/);
  
  if (match) {
    const [, field, value] = match;
    const fieldParts = field.split('.');
    const fieldName = fieldParts[fieldParts.length - 1] === 'to' || fieldParts[fieldParts.length - 1] === 'from'
      ? fieldParts[fieldParts.length - 2] 
      : fieldParts[fieldParts.length - 1];
    
    return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} → ${value}`;
  }
  
  return filter.name || filter.parent || 'Change Log Filter';
};


// Build options map from filter options (traverses childComponents)
// Stores both exact key and lowercase key for flexible matching
function buildOptionsMap(filterOptions: any[]): Record<string, string> {
  const map: Record<string, string> = {};
  const traverse = (list: any[]) => {
    if (!Array.isArray(list)) return;
    list.forEach((o: any) => {
      if (!o) return;
      const label = o.label || o.name || '';
      const exactKey = String(o.value || o.label || '');
      const lowerKey = exactKey.toLowerCase();
      // Store both exact and lowercase keys for flexible matching
      if (exactKey) map[exactKey] = label;
      if (lowerKey && lowerKey !== exactKey) map[lowerKey] = label;
      if (Array.isArray(o.childComponents) && o.childComponents.length > 0) {
        traverse(o.childComponents);
      }
    });
  };
  traverse(filterOptions);
  return map;
}

// Get options from filtersConfig for specific filters
function getFilterOptions(filterName: string, filtersConfig: any): any[] {
  if (!filtersConfig) return [];
  
  // Check idea section first, then evidence
  const ideaFilter = filtersConfig?.idea?.[filterName];
  if (ideaFilter?.options && Array.isArray(ideaFilter.options)) {
    return ideaFilter.options;
  }
  
  const evidenceFilter = filtersConfig?.evidence?.[filterName];
  if (evidenceFilter?.options && Array.isArray(evidenceFilter.options)) {
    return evidenceFilter.options;
  }
  
  return [];
}

function formatFilterValues(filter: ActiveFilter, filtersConfig?: any): string {
  const { selected, options, query } = filter;
  
  // Handle change log filters specially
  if (isChangeLogFilter(filter)) {
    const safeSelected = Array.isArray(selected) ? selected : [];
    if (safeSelected.length > 0 && safeSelected[0]) {
      const dateRangeKey = safeSelected[0];
      // Check if it's a dynamic date key
      const dynamicLabel = formatDynamicDateLabel(dateRangeKey);
      if (dynamicLabel !== dateRangeKey) {
        return dynamicLabel;
      } else if (safeSelected.length === 2) {
        // Explicit date range
        return `${formatDateValue(safeSelected[0])} – ${formatDateValue(safeSelected[1])}`;
      }
    }
    return 'All Time';
  }
  
  if (selected == null && query == null) return '—';
  if (filter.type === 'date' || filter.type === 'date-range') {
    // Check for dynamic date (savedKey or operator as string without selected array)
    const savedKey = (filter as any).savedKey || filter.operator;
    if (typeof savedKey === 'string' && (!Array.isArray(selected) || selected.length === 0)) {
      // Dynamic date - check if it's a known dynamic key
      const dynamicLabel = formatDynamicDateLabel(savedKey);
      if (dynamicLabel !== savedKey) {
        return dynamicLabel;
      }
    }
    
    // Handle different date filter formats:
    // 1. selected is an array [startDate, endDate] (evidence date filters)
    // 2. selected is an object { values: [startDate, endDate] } (idea date filters)
    // 3. query has { values: [startDate, endDate] }
    let values: [string, string] | undefined;
    
    if (Array.isArray(selected) && selected.length === 2) {
      // Format 1: selected is directly [startDate, endDate]
      values = selected as [string, string];
    } else if (selected && typeof selected === 'object' && 'values' in selected) {
      // Format 2: selected is { values: [startDate, endDate] }
      values = (selected as { values?: [string, string] }).values;
    } else if (query?.values) {
      // Format 3: query has the values
      values = query.values;
    }
    
    if (values?.length) {
      return values.map((v: string) => formatDateValue(v)).join(' – ');
    }
    return '—';
  }
  if (filter.type === 'boolean') {
    // Check for operations in filterConfig to get proper label
    if (filtersConfig && filter.operator !== undefined) {
      const filterName = filter.name;
      const filterConfig = filtersConfig?.idea?.[filterName] || filtersConfig?.evidence?.[filterName];
      if (filterConfig?.operations) {
        const operation = filterConfig.operations.find((op: any) =>
          JSON.stringify(op.value) === JSON.stringify(filter.operator)
        );
        if (operation?.label) return operation.label;
      }
    }
    // Fallback to Yes/No based on operator value (since boolean stores value in operator)
    const boolValue = filter.operator ?? selected;
    return boolValue ? 'Yes' : 'No';
  }
  if ((filter.type as string) === 'userIdIn') {
    // Handle userIdIn type similar to boolean - check operations
    if (filtersConfig && filter.operator !== undefined) {
      const filterName = filter.name;
      const filterConfig = filtersConfig?.idea?.[filterName] || filtersConfig?.evidence?.[filterName];
      if (filterConfig?.operations) {
        const operation = filterConfig.operations.find((op: any) =>
          JSON.stringify(op.value) === JSON.stringify(filter.operator)
        );
        if (operation?.label) return operation.label;
      }
    }
    return String(filter.operator ?? '—');
  }
  if (filter.type === 'number') {
    // Format number values with formatBigMonetaryValue
    if (Array.isArray(selected) && selected.length > 0) {
      const numValue = Number(selected[0]);
      return !isNaN(numValue) ? (formatBigMonetaryValue(numValue, '') ?? String(numValue)) : String(selected[0]);
    }
    if (selected != null) {
      const numValue = Number(selected);
      return !isNaN(numValue) ? (formatBigMonetaryValue(numValue, '') ?? String(numValue)) : String(selected);
    }
    return '—';
  }
  if (Array.isArray(selected)) {
    if (!selected.length) return '—';
    
    // Check if we need to look up options from filtersConfig
    // This handles filters with optionsKeyAppContext, dbFieldPath, or empty options
    const filterHasEmptyOptions = !options || (typeof options === 'object' && Object.keys(options).length === 0);
    const needsConfigLookup = filtersConfig && (
      filterHasEmptyOptions || 
      (filter as any).optionsKeyAppContext || 
      (filter as any).dbFieldPath
    );
    
    if (needsConfigLookup) {
      // Try to get options from filtersConfig using filter name or parent
      const filterName = filter.name || (filter as any).parent;
      const filterOptions = getFilterOptions(filterName, filtersConfig);
      if (filterOptions.length > 0) {
        const optionsMap = buildOptionsMap(filterOptions);
        const labels = selected.map((s) => {
          // Check if it's a special dynamic key first
          const specialLabel = formatCheckboxValue(String(s));
          if (specialLabel !== String(s)) return specialLabel;
          // Try both exact match and lowercase match
          return optionsMap[String(s)] || optionsMap[String(s).toLowerCase()] || String(s);
        });
        return labels.join(', ');
      }
    }
    
    // Apply special checkbox value formatting for all array selections
    const labels = options && Object.keys(options).length > 0
      ? selected.map((id) => {
        const specialLabel = formatCheckboxValue(String(id));
        if (specialLabel !== String(id)) return specialLabel;
        return options[id] ?? id;
      }).filter(Boolean)
      : selected.map((s) => {
        const specialLabel = formatCheckboxValue(String(s));
        if (specialLabel !== String(s)) return specialLabel;
        return String(s);
      });
    return labels.join(', ');
  }
  if (options && selected != null) return options[String(selected)] ?? String(selected);
  return String(selected ?? '—');
}

export interface SavedFiltersTooltipProps {
  filters: ActiveFilter[];
  filtersConfig?: any;
}

const SavedFiltersTooltip: React.FC<SavedFiltersTooltipProps> = ({
  filters,
  filtersConfig,
}) => {
  const validFilters = (filters?.filter((filter) => {
    if (filter.name === 'state' && filter.filterObject === 'evidence') return false;
    if (filter.selected === undefined) return false;
    if (Array.isArray(filter.selected) && filter.selected.length === 0) return false;
    return true;
  }) || []);

  if (!validFilters.length) {
    return (
      <Box py={6} px={12} sx={{ backgroundColor: '#1a1b1e', borderRadius: 4 }}>
        <Text size="xs" color="#fff">
          No filters applied
        </Text>
      </Box>
    );
  }

  return (
    <Box mt={2} py={8} px={4} sx={{ backgroundColor: '#1a1b1e', borderRadius: 4, maxHeight: '30vh', maxWidth: 350, overflow: 'hidden' }}>
      <ScrollArea type="auto" scrollbarSize={4} sx={{ flex: 1, minHeight: 0 }}>
        {validFilters.map((filter) => {
          const values = formatFilterValues(filter, filtersConfig);
          const filterLabel = isChangeLogFilter(filter) 
            ? formatChangeLogFilterLabel(filter)
            : (capitalizeFirstLetter(filter.label) || filter.name);
          return (
            <Flex
              key={filter.name}
              align="center"
              py={4}
              px={8}
              gap={6}
            >
              <Text size="xs" sx={{ whiteSpace: 'nowrap', color: '#fff' }}>
                {filterLabel}:
              </Text>
              <Text size="xs" truncate sx={{ color: '#fff' }} maw={100}>
                {values}
              </Text>
            </Flex>
          );
        })}
      </ScrollArea>
    </Box>
  );
};

export default SavedFiltersTooltip;